import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  Stack,
  Avatar,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddRounded as AddIcon,
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
  KeyRounded as KeyIcon,
  AdminPanelSettingsRounded as AdminIcon,
  SupervisorAccountRounded as ManagerIcon,
  PersonRounded as EmployeeIcon,
  ArticleRounded as BlogIcon,
  AssignmentIndRounded as ClaimsIcon,
  ManageAccountsRounded as UsersIcon,
} from '@mui/icons-material';
import authService from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import PageHeader from '../../components/ui/PageHeader';
import SearchField from '../../components/ui/SearchField';
import StatusChip from '../../components/ui/StatusChip';
import EmptyState from '../../components/ui/EmptyState';
import LoadingState from '../../components/ui/LoadingState';

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee', description: 'Dashboard access only' },
  { value: 'claims_manager', label: 'Claims Manager', description: 'Customers, emergencies, appointments' },
  { value: 'blog_manager', label: 'Blog Manager', description: 'Blog content management' },
  { value: 'manager', label: 'Manager', description: 'Full operations access' },
  { value: 'admin', label: 'Administrator', description: 'Everything, including users' },
];

const ROLE_LABEL = {
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
  blog_manager: 'Blog Manager',
  claims_manager: 'Claims Manager',
};

const UsersList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
  });
  const [newPassword, setNewPassword] = useState('');

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await authService.getAllUsers();
        setUsers(data);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      const filtered = users.filter(
        (user) =>
          user.name.toLowerCase().includes(lowerCaseSearch) ||
          user.email.toLowerCase().includes(lowerCaseSearch) ||
          user.role.toLowerCase().includes(lowerCaseSearch)
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
    setPage(0);
  }, [searchTerm, users]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Dialog handlers
  const handleOpenUserDialog = (user = null) => {
    setDialogError(null);
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        password: '', // Don't set password when editing
      });
    } else {
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'employee',
      });
    }
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setSelectedUser(null);
  };

  const handleOpenPasswordDialog = (user) => {
    setDialogError(null);
    setSelectedUser(user);
    setNewPassword('');
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setSelectedUser(null);
  };

  const handleOpenDeleteDialog = (user) => {
    setDialogError(null);
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submit (create/update user)
  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setDialogLoading(true);
    setDialogError(null);

    try {
      if (selectedUser) {
        // Update user
        const userData = { ...formData };
        delete userData.password; // Remove password field for updates
        await authService.updateUser(selectedUser._id, userData);
      } else {
        // Create user
        await authService.registerUser(formData);
      }

      // Refresh users list
      const data = await authService.getAllUsers();
      setUsers(data);

      handleCloseUserDialog();
    } catch (err) {
      console.error('Error submitting user:', err);
      setDialogError(err.response?.data?.error || 'An error occurred');
    } finally {
      setDialogLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setDialogError('Password must be at least 6 characters long');
      return;
    }

    setDialogLoading(true);
    setDialogError(null);

    try {
      await authService.resetPassword(selectedUser._id, newPassword);
      handleClosePasswordDialog();
    } catch (err) {
      console.error('Error resetting password:', err);
      setDialogError(err.response?.data?.error || 'An error occurred');
    } finally {
      setDialogLoading(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    setDialogLoading(true);
    setDialogError(null);

    try {
      await authService.deleteUser(selectedUser._id);

      // Refresh users list
      const data = await authService.getAllUsers();
      setUsers(data);

      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting user:', err);
      setDialogError(err.response?.data?.error || 'An error occurred');
    } finally {
      setDialogLoading(false);
    }
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'manager':
        return <ManagerIcon fontSize="small" />;
      case 'blog_manager':
        return <BlogIcon fontSize="small" />;
      case 'claims_manager':
        return <ClaimsIcon fontSize="small" />;
      case 'employee':
      default:
        return <EmployeeIcon fontSize="small" />;
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'primary';
      case 'blog_manager':
        return 'success';
      case 'claims_manager':
        return 'info';
      case 'employee':
      default:
        return 'default';
    }
  };

  if (loading) {
    return <LoadingState label="Loading users…" />;
  }

  const pagedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderUserActions = (user) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title="Edit user">
        <IconButton size="small" onClick={() => handleOpenUserDialog(user)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Reset password">
        <IconButton size="small" onClick={() => handleOpenPasswordDialog(user)}>
          <KeyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {currentUser?._id !== user._id && (
        <Tooltip title="Delete user">
          <IconButton
            size="small"
            sx={{ color: 'error.main' }}
            onClick={() => handleOpenDeleteDialog(user)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Manage team access and roles"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenUserDialog()}>
            Add User
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or role…"
          sx={{ maxWidth: { sm: 420 } }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {filteredUsers.length === 0 ? (
        <Card>
          <EmptyState
            icon={<UsersIcon />}
            title={searchTerm ? 'No matching users' : 'No users yet'}
            description={
              searchTerm ? 'Try a different search term.' : 'Add your first team member to get started.'
            }
            action={
              !searchTerm && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenUserDialog()}>
                  Add First User
                </Button>
              )
            }
          />
        </Card>
      ) : isMobile ? (
        /* ------------------------- Mobile card list ------------------------- */
        <Stack spacing={1.5}>
          {pagedUsers.map((user) => (
            <Card key={user._id} sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: alpha(
                      theme.palette[getRoleColor(user.role) === 'default' ? 'primary' : getRoleColor(user.role)]
                        .main,
                      0.14
                    ),
                    color: `${getRoleColor(user.role) === 'default' ? 'primary' : getRoleColor(user.role)}.main`,
                  }}
                >
                  {(user.name || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {user.name}
                    {currentUser?._id === user._id && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        (you)
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <StatusChip
                  status={user.role}
                  color={getRoleColor(user.role) === 'default' ? undefined : getRoleColor(user.role)}
                  label={ROLE_LABEL[user.role] || user.role}
                  icon={getRoleIcon(user.role)}
                />
                {renderUserActions(user)}
              </Stack>
            </Card>
          ))}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Per page:"
          />
        </Stack>
      ) : (
        /* --------------------------- Desktop table -------------------------- */
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedUsers.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: '0.9rem',
                            bgcolor: alpha(
                              theme.palette[
                                getRoleColor(user.role) === 'default' ? 'primary' : getRoleColor(user.role)
                              ].main,
                              0.14
                            ),
                            color: `${
                              getRoleColor(user.role) === 'default' ? 'primary' : getRoleColor(user.role)
                            }.main`,
                          }}
                        >
                          {(user.name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name}
                          {currentUser?._id === user._id && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                              (you)
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status={user.role}
                        color={getRoleColor(user.role) === 'default' ? undefined : getRoleColor(user.role)}
                        label={ROLE_LABEL[user.role] || user.role}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{renderUserActions(user)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Create / Edit user dialog */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmitUser}>
          <DialogTitle>{selectedUser ? 'Edit user' : 'Add new user'}</DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="Full name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="Email address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                required
                fullWidth
                autoComplete="off"
              />
              {!selectedUser && (
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  required
                  fullWidth
                  autoComplete="new-password"
                  helperText="At least 6 characters"
                />
              )}
              <FormControl>
                <FormLabel sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 600 }}>Role</FormLabel>
                <RadioGroup name="role" value={formData.role} onChange={handleFormChange}>
                  <Stack spacing={0.75}>
                    {ROLE_OPTIONS.map((option) => (
                      <Box
                        key={option.value}
                        sx={{
                          border: '1px solid',
                          borderColor:
                            formData.role === option.value ? 'primary.main' : 'divider',
                          borderRadius: '12px',
                          px: 1.5,
                          py: 0.5,
                          transition: 'border-color 160ms ease, background-color 160ms ease',
                          bgcolor:
                            formData.role === option.value
                              ? (t) => alpha(t.palette.primary.main, 0.05)
                              : 'transparent',
                        }}
                      >
                        <FormControlLabel
                          value={option.value}
                          control={<Radio size="small" />}
                          sx={{ width: '100%', m: 0 }}
                          label={
                            <Box sx={{ py: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleCloseUserDialog} color="inherit" disabled={dialogLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={dialogLoading}
              startIcon={dialogLoading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {dialogLoading ? 'Saving…' : selectedUser ? 'Save changes' : 'Create user'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for <strong>{selectedUser?.name}</strong>.
          </Typography>
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            helperText="At least 6 characters"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClosePasswordDialog} color="inherit" disabled={dialogLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {dialogLoading ? 'Resetting…' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete user dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently remove the account and cannot be undone.
          </Typography>
          {selectedUser && (
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                border: (t) => `1px solid ${alpha(t.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="subtitle2">{selectedUser.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" disabled={dialogLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ backgroundImage: 'none' }}
          >
            {dialogLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersList;
